import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CORPUS_SIZE } from "../../data/corpus";
import MangoLogo from "../../components/MangoLogo";

type TabKey = "daily" | "quiz" | "import" | "analytics";
type BillingCycle = "monthly" | "yearly";

export default function Welcome() {
  const navigate = useNavigate();

  // Dark / Light Theme
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark") ||
        (localStorage.getItem("mangoword-theme") ?? localStorage.getItem("cibook-theme")) === "dark"
      : true
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("mangoword-theme", isDark ? "dark" : "light");
    localStorage.setItem("cibook-theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Interactive Live Demo in SaaS Window
  const [activeTab, setActiveTab] = useState<TabKey>("quiz");
  const [quizAnswered, setQuizAnswered] = useState<boolean | null>(null);
  const [selectedOpt, setSelectedOpt] = useState<number | null>(null);

  // Pricing Toggle
  const [billing, setBilling] = useState<BillingCycle>("yearly");

  // FAQ Accordion
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-300 ${
        isDark ? "bg-slate-950 text-slate-100" : "bg-white text-slate-900"
      }`}
    >
      {/* ─── Modern SaaS Background Grid with Radial Fade ──────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div
          className={`absolute inset-0 opacity-[0.035] ${
            isDark ? "invert" : ""
          }`}
          style={{
            backgroundImage: `radial-gradient(#000 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
          }}
        />
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[450px] rounded-full blur-[140px] pointer-events-none animate-glow-drift ${
            isDark ? "bg-amber-500/10 opacity-30" : "bg-amber-500/10 opacity-40"
          }`}
        />
        <div
          className={`absolute top-[380px] -left-32 w-[500px] h-[320px] rounded-full blur-[130px] pointer-events-none animate-float-delayed ${
            isDark ? "bg-emerald-500/10 opacity-25" : "bg-emerald-500/10 opacity-35"
          }`}
        />
      </div>

      {/* ─── Sticky SaaS Floating Navbar ──────────────────────────────── */}
      <header
        className={`sticky top-0 z-50 w-full backdrop-blur-md border-b transition-colors duration-300 ${
          isDark ? "bg-slate-950/80 border-slate-800" : "bg-white/80 border-slate-200"
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-5 h-[64px] flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="no-underline text-current group flex items-center">
            <MangoLogo size={30} />
          </Link>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-8 text-[13.5px] font-medium text-current/70">
            <a href="#product" className="hover:text-current transition-colors no-underline text-current">
              產品功能
            </a>
            <a href="#methodology" className="hover:text-current transition-colors no-underline text-current">
              間隔演算法
            </a>
            <a href="#comparison" className="hover:text-current transition-colors no-underline text-current">
              功能對比
            </a>
            <a href="#pricing" className="hover:text-current transition-colors no-underline text-current">
              方案定價
            </a>
            <a href="#faq" className="hover:text-current transition-colors no-underline text-current">
              FAQ
            </a>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setIsDark(!isDark)}
              className={`w-8 h-8 rounded-lg border flex items-center justify-center text-[13px] cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95 ${
                isDark
                  ? "bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-700"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
              title={isDark ? "切換至淺色" : "切換至深色"}
              aria-label="Toggle theme"
            >
              {isDark ? "☀" : "☾"}
            </button>

            {/* Temporary Test Mode: Skip level test & directly go to login/reg */}
            <Link
              to="/login?test_mode=1"
              className={`text-[12px] font-grotesk font-semibold px-2.5 sm:px-3 py-1.5 rounded-full border border-dashed flex items-center gap-1.5 no-underline transition-all duration-200 hover:scale-105 active:scale-95 shadow-2xs ${
                isDark
                  ? "border-[#F59E0B]/60 bg-[#F59E0B]/15 text-[#FBBF24] hover:bg-[#F59E0B]/25 hover:border-[#F59E0B]"
                  : "border-[#D97706]/60 bg-[#FEF3C7] text-[#B45309] hover:bg-[#FDE68A] hover:border-[#D97706]"
              }`}
              title="測試模式：略過測驗，直接登入/註冊開始使用"
            >
              <span>🧪</span>
              <span className="whitespace-nowrap">測試模式</span>
            </Link>

            <Link
              to="/login"
              className="text-[13.5px] font-medium px-3 py-1.5 rounded-lg text-current/80 hover:text-current no-underline hidden sm:inline-block transition-colors"
            >
              登入
            </Link>

            <button
              type="button"
              onClick={() => navigate("/level-test/intro")}
              className={`font-grotesk text-[13px] font-bold px-3.5 sm:px-4 py-2 rounded-full cursor-pointer transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] shadow-sm whitespace-nowrap ${
                isDark
                  ? "bg-white text-slate-950 hover:bg-slate-100 hover:shadow-[0_0_20px_rgba(255,255,255,0.18)]"
                  : "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
              }`}
            >
              免費開始使用
            </button>
          </div>
        </div>
      </header>

      {/* ─── Hero Section ────────────────────────────────────────────── */}
      <section className="relative z-10 pt-16 md:pt-24 pb-16 px-5">
        <div className="max-w-[1200px] mx-auto text-center flex flex-col items-center">
          {/* Top Announcement Pill */}
          <Link
            to="/level-test/intro"
            className={`animate-fade-in-up inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-medium border mb-8 no-underline text-current transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] ${
              isDark
                ? "bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-700"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400 shadow-xs"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#2E6B4E] animate-pulse" />
            <span>2027 考綱詞庫與新演算法已發布</span>
            <span className="font-grotesk text-current/50">→</span>
          </Link>

          {/* SaaS Hero Title */}
          <h1 className="animate-fade-in-up [animation-delay:120ms] text-[clamp(38px,6vw,68px)] font-bold tracking-[-0.035em] leading-[1.1] m-0 max-w-[900px]">
            在考期之前，把{" "}
            <span
              className={`bg-clip-text text-transparent bg-gradient-to-r ${
                isDark
                  ? "from-white via-slate-200 to-emerald-400"
                  : "from-slate-950 via-slate-800 to-emerald-700"
              }`}
            >
              {CORPUS_SIZE.toLocaleString("en-US")} 個核心生字
            </span>{" "}
            真正刻進記憶。
          </h1>

          {/* Subtitle */}
          <p
            className={`animate-fade-in-up [animation-delay:220ms] mt-6 mb-10 text-[17px] md:text-[19px] leading-[1.65] max-w-[660px] ${
              isDark ? "text-slate-400" : "text-slate-600"
            } [text-wrap:pretty]`}
          >
            輸入考試日期，Mango Word 依間隔重複（SRS）算妥每日配額。
            答錯的詞自動退回複習，不用每天手動排進度表。
          </p>

          {/* CTAs */}
          <div className="animate-fade-in-up [animation-delay:320ms] flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate("/level-test/intro")}
              className={`w-full sm:w-auto font-grotesk text-[15.5px] font-bold px-8 py-4 rounded-full cursor-pointer transition-all duration-200 hover:scale-105 active:scale-[0.98] ${
                isDark
                  ? "bg-white text-slate-950 hover:bg-slate-100 shadow-[0_8px_30px_rgba(255,255,255,0.12)]"
                  : "bg-slate-900 text-white hover:bg-slate-800 shadow-[0_8px_24px_rgba(0,0,0,0.16)]"
              }`}
            >
              開始 3 分鐘水平測驗 →
            </button>

            <a
              href="#product"
              className={`w-full sm:w-auto font-grotesk text-[14.5px] font-medium px-6 py-4 rounded-full border text-center no-underline transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                isDark
                  ? "bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              查看系統架構 ↓
            </a>
          </div>

          {/* Micro Trust Proof */}
          <div className="animate-fade-in-up [animation-delay:420ms] mt-8 flex items-center gap-6 text-[12.5px] text-current/55 font-grotesk">
            <span>✓ 免費免綁卡</span>
            <span>✓ 支援 PWA 離線安裝</span>
            <span>✓ 覆蓋 DSE · IELTS · TOEFL</span>
          </div>
        </div>
        {/* ─── Hero App Window Mockup (High-End SaaS Interface) ──────── */}
        <div id="product" className="relative max-w-[1100px] mx-auto mt-16 px-2 sm:px-0 animate-fade-in-up [animation-delay:520ms]">
          {/* Left Floating Stat Chip */}
          <div className="hidden lg:flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-[0_12px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55)] backdrop-blur-md absolute -left-8 top-16 z-20 animate-float pointer-events-none">
            <div className="w-9 h-9 rounded-xl bg-[#2E6B4E]/15 dark:bg-[#9EE493]/15 flex items-center justify-center text-[#2E6B4E] dark:text-[#9EE493] text-lg font-bold">
              🎯
            </div>
            <div className="text-left">
              <div className="font-grotesk font-bold text-[13.5px] leading-tight flex items-center gap-1.5">
                <span>96.4%</span>
                <span className="text-[10px] font-semibold text-[#2E6B4E] dark:text-[#9EE493] bg-[#2E6B4E]/10 dark:bg-[#9EE493]/10 px-1.5 py-0.5 rounded">
                  +18.2%
                </span>
              </div>
              <div className="text-[11px] text-current/60 font-medium">SRS 記憶留存率</div>
            </div>
          </div>

          {/* Right Floating Stat Chip */}
          <div className="hidden lg:flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-[0_12px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55)] backdrop-blur-md absolute -right-8 bottom-20 z-20 animate-float-delayed pointer-events-none">
            <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B] text-lg font-bold">
              ⚡
            </div>
            <div className="text-left">
              <div className="font-grotesk font-bold text-[13.5px] leading-tight">
                每日 8 分鐘
              </div>
              <div className="text-[11px] text-current/60 font-medium">考前自動換算最佳配額</div>
            </div>
          </div>

          <div
            className={`rounded-2xl border overflow-hidden shadow-2xl transition-all ${
              isDark
                ? "bg-slate-950 border-slate-800 shadow-[0_25px_80px_rgba(0,0,0,0.85)]"
                : "bg-white border-slate-200 shadow-[0_25px_70px_rgba(15,23,42,0.08)]"
            }`}
          >
            {/* Window Chrome Header */}
            <div
              className={`px-4 py-3 border-b flex items-center justify-between text-[12px] ${
                isDark ? "bg-slate-900 border-slate-800" : "bg-slate-100 border-slate-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FF5F56] border border-[#E0443E]/50" />
                <span className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]/50" />
                <span className="w-3 h-3 rounded-full bg-[#27C93F] border border-[#1AAB29]/50" />
                <span className="ml-3 font-grotesk text-current/50 font-medium">
                  mangoword.app / workspace
                </span>
              </div>
              <div className="flex items-center gap-1 font-grotesk text-[11px] text-current/60 bg-current/5 px-2.5 py-1 rounded-md">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2E6B4E] animate-pulse" />
                <span>Cloud Sync · D1 Connected</span>
              </div>
            </div>

            {/* App Workspace Body */}
            <div className="grid grid-cols-1 md:grid-cols-12 min-h-[460px]">
              {/* Sidebar */}
              <div
                className={`hidden md:flex md:col-span-3 border-r p-4 flex-col justify-between ${
                  isDark ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                }`}
              >
                <div>
                  <div className="text-[11px] font-grotesk font-bold tracking-widest text-current/40 uppercase mb-3 px-2">
                    學習模組
                  </div>
                  <div className="flex flex-col gap-1">
                    {[
                      { key: "quiz", label: "即時測驗與作答", badge: "Live" },
                      { key: "daily", label: "今日配額與進度", badge: "14 left" },
                      { key: "import", label: "試卷／講義匯入", badge: "PDF" },
                      { key: "analytics", label: "SRS 記憶曲線", badge: "96%" },
                    ].map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setActiveTab(item.key as TabKey)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-medium text-left cursor-pointer transition-all duration-200 ${
                          activeTab === item.key
                            ? isDark
                              ? "bg-slate-800 text-white font-semibold translate-x-1"
                              : "bg-slate-200 text-slate-900 font-semibold translate-x-1"
                            : "text-current/65 hover:bg-current/5 hover:translate-x-0.5"
                        }`}
                      >
                        <span>{item.label}</span>
                        <span
                          className={`text-[10px] font-grotesk font-semibold px-1.5 py-0.5 rounded transition-colors ${
                            activeTab === item.key
                              ? isDark
                                ? "bg-emerald-950 text-emerald-400"
                                : "bg-emerald-100 text-emerald-800"
                              : "bg-current/5 text-current/50"
                          }`}
                        >
                          {item.badge}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sidebar Bottom User pill */}
                <div className="p-3 rounded-xl border border-current/10 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#2E6B4E] text-white flex items-center justify-center font-grotesk text-[12px] font-bold">
                    S
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold truncate">Student Pro</div>
                    <div className="text-[10.5px] text-current/50 font-grotesk">HKDSE 2027 · Day 18</div>
                  </div>
                </div>
              </div>

              {/* Main App Content Area */}
              <div className="col-span-1 md:col-span-9 p-6 md:p-8 flex flex-col justify-between overflow-hidden">
                {activeTab === "quiz" && (
                  <div key="tab-quiz" className="h-full flex flex-col justify-between animate-pop-spring">
                    <div>
                      <div className="flex items-center justify-between pb-3 border-b border-current/10">
                        <span className="font-grotesk text-[12px] text-current/60 font-medium">
                          HKDSE 卷二高頻考點 · 單詞認讀測驗
                        </span>
                        <span className="font-grotesk text-[12px] text-[#C8442A] font-semibold">
                          第 1 題 / 共 14 題
                        </span>
                      </div>

                      <div className="text-center py-6">
                        <div className="font-grotesk text-[36px] font-bold tracking-tight">
                          candid
                        </div>
                        <div className="font-serif italic text-[15px] text-current/60 mt-1">
                          /ˈkændɪd/ · adj.
                        </div>
                      </div>

                      {/* Options */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-[620px] mx-auto">
                        {[
                          { text: "坦率的、直言不諱的", correct: true },
                          { text: "謹慎的、含蓄的", correct: false },
                          { text: "模糊不清的", correct: false },
                          { text: "不可避免的", correct: false },
                        ].map((opt, idx) => {
                          const isPicked = selectedOpt === idx;
                          let btnStyle = isDark
                            ? "bg-slate-900 border-slate-800 hover:border-slate-700"
                            : "bg-white border-slate-200 hover:border-slate-400";

                          if (quizAnswered !== null && isPicked) {
                            btnStyle = opt.correct
                              ? "bg-emerald-950/40 border-emerald-500 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.25)]"
                              : "bg-rose-950/40 border-rose-500 text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.25)]";
                          } else if (quizAnswered !== null && opt.correct) {
                            btnStyle = "bg-emerald-950/40 border-emerald-500 text-emerald-300";
                          }

                          return (
                            <button
                              key={opt.text}
                              type="button"
                              onClick={() => {
                                if (quizAnswered !== null) return;
                                setSelectedOpt(idx);
                                setQuizAnswered(opt.correct);
                              }}
                              className={`p-3.5 rounded-xl border text-left flex items-center gap-3 cursor-pointer transition-all duration-200 hover:scale-[1.015] active:scale-[0.985] text-[13.5px] font-medium ${btnStyle} ${
                                quizAnswered !== null && isPicked ? "animate-pop-spring" : ""
                              }`}
                            >
                              <span className="w-5 h-5 rounded-md bg-current/10 flex items-center justify-center font-grotesk text-[11px] font-bold">
                                {"ABCD"[idx]}
                              </span>
                              <span className="flex-1">{opt.text}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Result Bar */}
                    <div className="mt-6 pt-4 border-t border-current/10 flex items-center justify-between text-[13px]">
                      {quizAnswered === null ? (
                        <span className="text-current/50 font-grotesk text-[12px]">
                          點選上方選項體驗即時測驗反饋
                        </span>
                      ) : (
                        <span
                          className={`font-semibold animate-pop-spring ${
                            quizAnswered ? "text-[#4ADE80]" : "text-[#F87171]"
                          }`}
                        >
                          {quizAnswered
                            ? "✓ 答對！下次複習間隔自動擴展至 4 天後"
                            : "✕ 答錯！系統自動退回 10 分鐘後重考"}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setQuizAnswered(null);
                          setSelectedOpt(null);
                        }}
                        className="text-current/60 hover:text-current underline cursor-pointer text-[12px] transition-colors"
                      >
                        重置體驗
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "daily" && (
                  <div key="tab-daily" className="flex flex-col justify-between h-full animate-pop-spring">
                    <div>
                      <div className="font-grotesk text-[12px] font-bold tracking-wider text-current/50 uppercase mb-2">
                        智能每日排程
                      </div>
                      <h3 className="text-[22px] font-bold m-0">
                        HKDSE 英文卷二 · 倒數 247 天
                      </h3>
                      <p className="text-[14px] text-current/60 mt-1">
                        系統已根據你的 82% 正確率動態校準每日記憶量。
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                        <div className="p-4 rounded-xl border border-current/10 bg-current/5 transition-transform hover:scale-[1.02]">
                          <div className="text-[12px] text-current/60">今日新詞配額</div>
                          <div className="font-grotesk text-[32px] font-bold mt-1 text-[#2E6B4E] dark:text-[#9EE493]">
                            14
                          </div>
                          <div className="text-[11px] text-current/50 mt-1">約 6 分鐘即可完成</div>
                        </div>

                        <div className="p-4 rounded-xl border border-current/10 bg-current/5 transition-transform hover:scale-[1.02]">
                          <div className="text-[12px] text-current/60">舊詞間隔複習</div>
                          <div className="font-grotesk text-[32px] font-bold mt-1">
                            38
                          </div>
                          <div className="text-[11px] text-current/50 mt-1">鞏固即將遺忘臨界點</div>
                        </div>

                        <div className="p-4 rounded-xl border border-current/10 bg-current/5 transition-transform hover:scale-[1.02]">
                          <div className="text-[12px] text-current/60">預計總完讀日</div>
                          <div className="font-grotesk text-[20px] font-bold mt-2">
                            2027 年 1 月
                          </div>
                          <div className="text-[11px] text-current/50 mt-1">比考期早 3 個月</div>
                        </div>
                      </div>

                      <div className="mt-6 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => navigate("/today")}
                          className="font-grotesk text-[13px] font-bold px-5 py-2.5 rounded-xl bg-[#2E6B4E] dark:bg-[#9EE493] text-white dark:text-[#0C1E14] cursor-pointer hover:opacity-90 hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 shadow-sm"
                        >
                          <span>進入今日學習排程</span>
                          <span>→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "import" && (
                  <div key="tab-import" className="flex flex-col justify-between h-full animate-pop-spring">
                    <div>
                      <div className="font-grotesk text-[12px] font-bold tracking-wider text-current/50 uppercase mb-2">
                        教材智能導入引擎
                      </div>
                      <h3 className="text-[22px] font-bold m-0">
                        上傳任何 Past Paper 或學校筆記
                      </h3>
                      <p className="text-[14px] text-current/60 mt-1">
                        支援 PDF、Word、Excel 及文章 URL，一鍵自動過濾已會單詞。
                      </p>

                      <div
                        onClick={() => navigate("/import")}
                        className="mt-6 border-2 border-dashed border-current/20 hover:border-[#2E6B4E] dark:hover:border-[#9EE493] rounded-2xl p-8 text-center flex flex-col items-center justify-center bg-current/5 hover:bg-current/10 cursor-pointer transition-all duration-200 hover:scale-[1.01] group"
                      >
                        <div className="w-10 h-10 rounded-full bg-current/10 flex items-center justify-center text-[18px] mb-3 group-hover:scale-115 transition-transform">
                          📄
                        </div>
                        <div className="text-[14px] font-semibold">點擊進入試卷講義智慧抽詞 →</div>
                        <div className="text-[12px] text-current/50 mt-1">支援 PDF、Word、Excel 及文章網址匯入</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "analytics" && (
                  <div key="tab-analytics" className="flex flex-col justify-between h-full animate-pop-spring">
                    <div>
                      <div className="font-grotesk text-[12px] font-bold tracking-wider text-current/50 uppercase mb-2">
                        SRS 記憶曲線監控
                      </div>
                      <h3 className="text-[22px] font-bold m-0">
                        96% 考前 5 輪記牢率
                      </h3>
                      <p className="text-[14px] text-current/60 mt-1">
                        連續答對 5 次即判定為永久掌握，答錯自動觸發重測。
                      </p>

                      <div className="mt-6 flex items-end gap-3 h-[140px] pt-6 border-b border-current/10 pb-2">
                        {[
                          { label: "10分", h: "20%", val: "輪次 1" },
                          { label: "1天", h: "35%", val: "輪次 2" },
                          { label: "4天", h: "55%", val: "輪次 3" },
                          { label: "12天", h: "78%", val: "輪次 4" },
                          { label: "30天", h: "100%", val: "永久記憶" },
                        ].map((bar) => (
                          <div key={bar.label} className="flex-1 flex flex-col items-center gap-2 h-full justify-end group">
                            <div
                              className="w-full bg-[#2E6B4E] dark:bg-[#9EE493] rounded-t-lg transition-all duration-500 ease-out group-hover:opacity-80"
                              style={{ height: bar.h }}
                            />
                            <span className="font-grotesk text-[11px] text-current/60 group-hover:text-current transition-colors">{bar.label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => navigate("/stats")}
                          className="font-grotesk text-[13px] font-bold px-5 py-2.5 rounded-xl bg-current/10 hover:bg-current/15 text-current cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-1.5"
                        >
                          <span>查看完整記憶分析與統計</span>
                          <span>→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Bento Grid Architecture (Modern SaaS Feature Showcase) ── */}
      <section id="methodology" className={`py-24 px-5 border-t ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-slate-50"}`}>
        <div className="max-w-[1200px] mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-[680px] mx-auto mb-16">
            <div className="font-grotesk text-[11.5px] font-bold tracking-[0.2em] text-[#C8442A] uppercase mb-3">
              SYSTEM ARCHITECTURE
            </div>
            <h2 className="text-[clamp(30px,4.5vw,44px)] font-bold tracking-tight m-0 leading-tight">
              專為應考深度優化的認知工程架構
            </h2>
            <p className={`mt-4 text-[16px] leading-[1.65] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              傳統背單詞 App 依賴盲目打卡；Mango Word 結合神經認知學與考試倒數機制，把每個單詞鎖定在遺忘臨界點。
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Bento Card 1: Spaced Repetition Core (Large 8 cols) */}
            <div
              className={`md:col-span-8 p-8 rounded-3xl border flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                isDark
                  ? "bg-slate-900 border-slate-800 hover:border-slate-700"
                  : "bg-white border-slate-200 hover:border-slate-300 shadow-xs"
              }`}
            >
              <div>
                <span className="font-grotesk text-[11px] font-bold tracking-wider text-[#2E6B4E] dark:text-[#9EE493] uppercase">
                  ENGINE · 01
                </span>
                <h3 className="text-[24px] font-bold tracking-tight mt-2 mb-3">
                  SuperMemo 智能間隔演算法
                </h3>
                <p className={`text-[15px] leading-[1.7] max-w-[560px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  系統精確追蹤每一個單詞的「記憶衰退週期」。單詞每答對一次，下次複習間隔自動擴展（10分 ➔ 1天 ➔ 4天 ➔ 12天 ➔ 30天）。在臨界點喚醒記憶，鞏固率高達 96%。
                </p>
              </div>

              {/* Mini visual */}
              <div className="mt-8 pt-6 border-t border-current/10 flex flex-wrap gap-2.5">
                {["10 Min", "1 Day", "4 Days", "12 Days", "30 Days"].map((step, idx) => (
                  <div
                    key={step}
                    className={`font-grotesk text-[12px] font-semibold px-3 py-1.5 rounded-lg border transition-transform duration-200 hover:scale-105 cursor-default ${
                      idx === 4
                        ? "bg-[#2E6B4E] text-white border-[#2E6B4E]"
                        : isDark
                        ? "bg-slate-800 border-slate-700 text-slate-300"
                        : "bg-slate-100 border-slate-200 text-slate-700"
                    }`}
                  >
                    Cycle {idx + 1}: {step}
                  </div>
                ))}
              </div>
            </div>

            {/* Bento Card 2: Leech Isolation (4 cols) */}
            <div
              className={`md:col-span-4 p-8 rounded-3xl border flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                isDark
                  ? "bg-slate-900 border-slate-800 hover:border-slate-700"
                  : "bg-white border-slate-200 hover:border-slate-300 shadow-xs"
              }`}
            >
              <div>
                <span className="font-grotesk text-[11px] font-bold tracking-wider text-[#C8442A] uppercase">
                  DIAGNOSTIC · 02
                </span>
                <h3 className="text-[22px] font-bold tracking-tight mt-2 mb-3">
                  易忘詞隔離 (Leech Filter)
                </h3>
                <p className={`text-[14.5px] leading-[1.65] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  連續答錯 4 次的生字會被自動標註為「易忘詞」，不再以純選擇題放行，改以真題例句與深層配對專項強化。
                </p>
              </div>
              <div className="mt-6 p-3.5 rounded-xl border border-[#C8442A]/30 bg-[#C8442A]/10 text-[12.5px] text-[#C8442A] font-semibold font-grotesk flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#C8442A] animate-ping" />
                <span>檢測到 12 個高頻易忘生字 · 已分流強化</span>
              </div>
            </div>

            {/* Bento Card 3: Two-Tier Testing (4 cols) */}
            <div
              className={`md:col-span-4 p-8 rounded-3xl border flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                isDark
                  ? "bg-slate-900 border-slate-800 hover:border-slate-700"
                  : "bg-white border-slate-200 hover:border-slate-300 shadow-xs"
              }`}
            >
              <div>
                <span className="font-grotesk text-[11px] font-bold tracking-wider text-[#2E6B4E] dark:text-[#9EE493] uppercase">
                  MASTERY · 03
                </span>
                <h3 className="text-[22px] font-bold tracking-tight mt-2 mb-3">
                  二階認知：認出 ➔ 配對
                </h3>
                <p className={`text-[14.5px] leading-[1.65] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  新詞以單選題建立印象；第 3 次出現時換成 4 組配對題。要在相近選項中精準辨別，方能判定為真正掌握。
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between text-[12px] font-grotesk text-current/60 pt-4 border-t border-current/10">
                <span>MC 題 (1–2 次)</span>
                <span>➔</span>
                <span className="text-[#2E6B4E] dark:text-[#9EE493] font-bold">配對題 (第 3 次起)</span>
              </div>
            </div>

            {/* Bento Card 4: Universal File Ingestion (Large 8 cols) */}
            <div
              className={`md:col-span-8 p-8 rounded-3xl border flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                isDark
                  ? "bg-slate-900 border-slate-800 hover:border-slate-700"
                  : "bg-white border-slate-200 hover:border-slate-300 shadow-xs"
              }`}
            >
              <div>
                <span className="font-grotesk text-[11px] font-bold tracking-wider text-[#2E6B4E] dark:text-[#9EE493] uppercase">
                  INGESTION · 04
                </span>
                <h3 className="text-[24px] font-bold tracking-tight mt-2 mb-3">
                  任何 Past Paper 與筆記即抽即背
                </h3>
                <p className={`text-[15px] leading-[1.7] max-w-[560px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  支援 PDF、Word、Excel 及文章 URL 直接匯入。內建 NLP 斷詞引擎，自動剃除你已掌握的基礎字彙，僅提煉出未背過的考點生字，一鍵打包為自訂詞單。
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-current/10 flex flex-wrap items-center gap-3">
                {["PDF 試卷", "Word 補習講義", "Excel 詞表", "BBC / 網頁新聞"].map((tag) => (
                  <span
                    key={tag}
                    className="font-grotesk text-[12px] px-3 py-1 rounded-md bg-current/5 border border-current/10 font-medium transition-transform hover:scale-105 cursor-default"
                  >
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SaaS Comparison Matrix ──────────────────────────────────── */}
      <section id="comparison" className="py-24 px-5 border-t border-current/10">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center max-w-[620px] mx-auto mb-16">
            <div className="font-grotesk text-[11.5px] font-bold tracking-[0.2em] text-[#C8442A] uppercase mb-3">
              COMPARISON
            </div>
            <h2 className="text-[clamp(28px,4vw,40px)] font-bold tracking-tight m-0">
              為什麼傳統背詞法讓你事倍功半？
            </h2>
          </div>

          <div
            className={`rounded-3xl border overflow-hidden ${
              isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-[14px]">
                <thead>
                  <tr
                    className={`border-b text-[13px] font-grotesk ${
                      isDark ? "bg-slate-800/80 border-slate-700" : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <th className="p-4 md:p-5 font-bold">核心功能與機制</th>
                    <th className="p-4 md:p-5 text-current/60">實體單詞書</th>
                    <th className="p-4 md:p-5 text-current/60">通用 Anki 卡牌</th>
                    <th className="p-4 md:p-5 font-bold text-[#F59E0B] dark:text-[#FBBF24]">
                      Mango Word
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-current/10">
                  {[
                    {
                      feature: "依考試日期自動倒數計算每日配額",
                      book: "✕ 手動估計",
                      anki: "✕ 需手動自調",
                      mango: "✓ 全自動動態適配",
                    },
                    {
                      feature: "入學 3 分鐘自適應測驗跳過熟詞",
                      book: "✕ 從 A 開始硬背",
                      anki: "✕ 無起點測驗",
                      mango: "✓ 即時定點起跑",
                    },
                    {
                      feature: "二階認知題型（選擇認讀 ➔ 配對辨析）",
                      book: "✕ 單向死記",
                      anki: "✕ 僅單卡翻面",
                      mango: "✓ 深度認知閉環",
                    },
                    {
                      feature: "Past Paper 與講義 PDF 智能抽生字",
                      book: "✕ 不支援",
                      anki: "✕ 需手工轉 CSV",
                      mango: "✓ 一鍵智慧抽取",
                    },
                    {
                      feature: "連續答錯自動隔離強化（Leeches）",
                      book: "✕ 容易漏看",
                      anki: "△ 僅能手動標記",
                      mango: "✓ 自動弱項分流",
                    },
                  ].map((row) => (
                    <tr key={row.feature} className="hover:bg-current/[0.03] transition-colors">
                      <td className="p-4 md:p-5 font-medium">{row.feature}</td>
                      <td className="p-4 md:p-5 text-current/50">{row.book}</td>
                      <td className="p-4 md:p-5 text-current/50">{row.anki}</td>
                      <td className="p-4 md:p-5 font-bold text-[#F59E0B] dark:text-[#FBBF24]">
                        {row.mango}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SaaS Pricing Section ────────────────────────────────────── */}
      <section id="pricing" className={`py-24 px-5 border-t ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-slate-50"}`}>
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center max-w-[620px] mx-auto mb-12">
            <div className="font-grotesk text-[11.5px] font-bold tracking-[0.2em] text-[#C8442A] uppercase mb-3">
              TRANSPARENT PRICING
            </div>
            <h2 className="text-[clamp(28px,4vw,40px)] font-bold tracking-tight m-0">
              透明平實，無負擔備戰考期
            </h2>
            <p className={`mt-3 text-[15.5px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              核心詞庫全面免費開放，升級 PRO 享受無限試卷匯入與雲端備份。
            </p>

            {/* Sliding Pill Billing switcher */}
            <div className="mt-8 relative inline-flex items-center p-1 rounded-full border border-current/15 bg-current/5">
              <div
                className={`absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  billing === "monthly" ? "left-1 w-[88px]" : "left-[94px] w-[138px]"
                } ${isDark ? "bg-white" : "bg-slate-900"}`}
              />
              <button
                type="button"
                onClick={() => setBilling("monthly")}
                className={`relative z-10 font-grotesk text-[13px] font-semibold px-4 py-1.5 rounded-full cursor-pointer transition-colors duration-200 ${
                  billing === "monthly"
                    ? isDark ? "text-slate-950" : "text-white"
                    : "text-current/60 hover:text-current"
                }`}
              >
                月繳方案
              </button>
              <button
                type="button"
                onClick={() => setBilling("yearly")}
                className={`relative z-10 font-grotesk text-[13px] font-semibold px-4 py-1.5 rounded-full cursor-pointer transition-colors duration-200 flex items-center gap-1.5 ${
                  billing === "yearly"
                    ? isDark ? "text-slate-950" : "text-white"
                    : "text-current/60 hover:text-current"
                }`}
              >
                <span>年繳特惠</span>
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full transition-colors ${
                    billing === "yearly" ? "bg-[#2E6B4E] text-white" : "bg-current/10 text-current/70"
                  }`}
                >
                  省 40%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Free Tier */}
            <div
              className={`p-8 rounded-3xl border flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                isDark ? "bg-slate-900 border-slate-800 hover:border-slate-700" : "bg-white border-slate-200 hover:border-slate-300 shadow-sm"
              }`}
            >
              <div>
                <div className="font-grotesk font-bold text-[18px]">Student Basic</div>
                <div className="text-[13.5px] text-current/60 mt-1">適合剛開始建立字彙習慣的學生</div>
                <div className="font-grotesk text-[40px] font-bold mt-5 tracking-tight">
                  HK$ 0{" "}
                  <span className="text-[14px] font-normal text-current/50 font-sans">
                    / 永久免費
                  </span>
                </div>

                <div className="mt-8 flex flex-col gap-3.5 text-[13.5px]">
                  {[
                    "HKDSE 核心 3,000 詞完整題庫",
                    "SuperMemo 間隔重複智能排程",
                    "3 分鐘入學自適應水平測驗",
                    "支援 PWA 手機離線安裝",
                    "每月最多匯入 3 份試卷講義",
                  ].map((feat) => (
                    <div key={feat} className="flex items-center gap-3">
                      <span className="text-[#2E6B4E] dark:text-[#9EE493] font-bold">✓</span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/level-test/intro")}
                className={`w-full mt-8 py-3.5 rounded-full font-grotesk font-bold text-[14.5px] border cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                  isDark
                    ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                    : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                }`}
              >
                免費開始使用
              </button>
            </div>

            {/* Pro Tier (Highlighted) */}
            <div
              className={`p-8 rounded-3xl border flex flex-col justify-between relative transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl ${
                isDark
                  ? "bg-slate-900 border-emerald-500/50 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
                  : "bg-white border-[#2E6B4E] shadow-xl hover:shadow-2xl"
              }`}
            >
              <div className="absolute top-4 right-5 font-grotesk text-[10.5px] font-bold uppercase px-2.5 py-1 rounded-full bg-[#2E6B4E] text-white">
                RECOMMENDED
              </div>

              <div>
                <div className="font-grotesk font-bold text-[18px]">Exam Pass 考生通行證</div>
                <div className="text-[13.5px] text-current/60 mt-1">針對大考衝刺，全功能無上限</div>
                <div key={billing} className="font-grotesk text-[40px] font-bold mt-5 tracking-tight text-[#2E6B4E] dark:text-[#9EE493] animate-pop-spring">
                  {billing === "yearly" ? "HK$ 28" : "HK$ 48"}{" "}
                  <span className="text-[14px] font-normal text-current/50 font-sans">
                    / 月
                  </span>
                </div>

                <div className="mt-8 flex flex-col gap-3.5 text-[13.5px]">
                  {[
                    "包含 Basic 方案的所有功能",
                    "無限量 PDF、Word、Excel 及網址抽詞",
                    "解鎖 IELTS (4,500詞) 與 TOEFL (5,000詞)",
                    "Cloudflare D1 雲端多裝置即時同步",
                    "高頻易忘詞（Leeches）專項生成與深度分析",
                    "支援自定義每日複習提醒與考期微調",
                  ].map((feat) => (
                    <div key={feat} className="flex items-center gap-3">
                      <span className="text-[#2E6B4E] dark:text-[#9EE493] font-bold">✓</span>
                      <span className="font-medium">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/level-test/intro")}
                className={`w-full mt-8 py-3.5 rounded-full font-grotesk font-bold text-[14.5px] cursor-pointer transition-all duration-200 shadow-md hover:scale-[1.02] active:scale-[0.98] ${
                  isDark
                    ? "bg-white text-slate-950 hover:bg-slate-100"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                免費試用 7 天 PRO
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Frequently Asked Questions (FAQ) ────────────────────────── */}
      <section id="faq" className="py-24 px-5 border-t border-current/10">
        <div className="max-w-[760px] mx-auto">
          <div className="text-center mb-12">
            <div className="font-grotesk text-[11.5px] font-bold tracking-[0.2em] text-[#C8442A] uppercase mb-3">
              FAQ
            </div>
            <h2 className="text-[clamp(28px,3.8vw,36px)] font-bold tracking-tight m-0">
              常見問題解答
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {[
              {
                q: "Mango Word 和一般的單詞卡（Anki、Quizlet）有何差別？",
                a: "一般的單詞卡需要使用者自行排定每天背幾張卡，缺乏針對考試目標日期的倒數換算機制。Mango Word 專門為有明確考期（如 DSE、IELTS）的學生設計，由演算法倒推每日最佳新詞配額，並內建二階題型（選擇認讀 ➔ 四組配對）確保真正辨析。",
              },
              {
                q: "每天只需花 6–8 分鐘真的足夠應考嗎？",
                a: "足夠。根據認知負荷理論，一次性背 100 個生字絕大多數會在 48 小時內遺忘。Mango Word 透過間隔重複（10分 ➔ 1天 ➔ 4天 ➔ 12天 ➔ 30天）在遺忘點前精準複習，每天累積 10–14 個新詞，幾個月下來即能穩步累積 3,000+ 核心字彙。",
              },
              {
                q: "我需要一直連著網絡才能使用嗎？",
                a: "不需要。Mango Word 是標準 PWA（漸進式 Web 應用程式），支援離線快取與本機 IndexedDB 儲存。在港鐵或無網絡環境下皆可順暢做題，重新連線後自動同步至雲端。",
              },
              {
                q: "如何匯入我自己的補習班筆記或 Past Paper？",
                a: "在導航中進入「匯入」功能，上傳 PDF、Word 文件或直接貼上文章網址。系統會自動進行自然語言處理，剔除基礎日常詞，提煉出未掌握的考綱生字一鍵匯入。",
              },
            ].map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={faq.q}
                  className={`rounded-2xl border overflow-hidden transition-all duration-200 ${
                    isDark ? "bg-slate-900 border-slate-800 hover:border-slate-700" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer font-bold text-[15.5px] text-current"
                  >
                    <span>{faq.q}</span>
                    <span
                      className={`text-[20px] text-current/60 font-grotesk font-light transition-transform duration-300 select-none ${
                        isOpen ? "rotate-45" : "rotate-0"
                      }`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div
                        className={`px-5 pb-5 text-[14px] leading-[1.7] border-t pt-3 ${
                          isDark ? "text-slate-400 border-slate-800" : "text-slate-600 border-slate-100"
                        }`}
                      >
                        {faq.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Bottom High-Conversion SaaS CTA ────────────────────────── */}
      <section className="py-20 md:py-28 px-5">
        <div
          className={`max-w-[1200px] mx-auto rounded-3xl p-10 md:p-16 text-center border relative overflow-hidden ${
            isDark
              ? "bg-slate-900 border-slate-800"
              : "bg-slate-950 text-white border-transparent shadow-2xl"
          }`}
        >
          {/* Subtle Ambient Radial Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-[100px] bg-[#2E6B4E]/20 pointer-events-none animate-glow-drift" />

          <div className="max-w-[680px] mx-auto relative z-10">
            <h2 className="text-[clamp(28px,4.5vw,46px)] font-bold tracking-tight m-0 leading-[1.15]">
              掌握詞彙，是奪取考試星級最直接的槓桿。
            </h2>
            <p className="mt-4 mb-8 text-[16px] md:text-[17px] leading-[1.7] opacity-80">
              不用下載任何軟體，立即開始 3 分鐘免費自適應水平測驗，由 AI 測算出你的字彙量與考期專屬配額。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => navigate("/level-test/intro")}
                className="w-full sm:w-auto font-grotesk text-[16px] font-bold px-9 py-4 rounded-full bg-white text-slate-950 hover:bg-slate-100 hover:scale-105 active:scale-[0.98] transition-all duration-200 shadow-xl hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] cursor-pointer"
              >
                免費開始水平測驗 →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Modern Multi-Column SaaS Footer ─────────────────────────── */}
      <footer
        className={`py-14 px-5 border-t text-[13px] ${
          isDark
            ? "border-slate-800 bg-slate-950 text-slate-400"
            : "border-slate-200 bg-slate-50 text-slate-500"
        }`}
      >
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          {/* Brand info */}
          <div className="col-span-2">
            <Link to="/" className="mb-3 block no-underline text-current">
              <MangoLogo size={24} />
            </Link>
            <p className="text-[13px] leading-[1.6] max-w-[280px] opacity-75 m-0">
              專為考試倒數設計的智能間隔重複詞彙學習 PWA。
            </p>
            <a
              href="/api/health"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-[12px] opacity-75 hover:opacity-100 no-underline text-current transition-opacity group"
              title="點擊檢查即時 Cloudflare API 運行狀態"
            >
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="group-hover:underline">All Systems Operational ↗</span>
            </a>
          </div>

          {/* Column: 產品功能 */}
          <div>
            <div className="font-grotesk font-bold text-current text-[12px] uppercase tracking-wider mb-3">
              產品功能
            </div>
            <div className="flex flex-col gap-2.5 font-medium">
              <Link to="/level-test/intro" className="hover:text-current no-underline text-current/70 transition-colors">
                入學水平測驗
              </Link>
              <Link to="/import" className="hover:text-current no-underline text-current/70 transition-colors">
                試卷講義匯入
              </Link>
              <Link to="/wordbook" className="hover:text-current no-underline text-current/70 transition-colors">
                核心生字簿搜尋
              </Link>
              <a href="#methodology" className="hover:text-current no-underline text-current/70 transition-colors">
                間隔重複演算法
              </a>
              <a href="#pricing" className="hover:text-current no-underline text-current/70 transition-colors">
                方案與定價
              </a>
            </div>
          </div>

          {/* Column: 支援詞庫 */}
          <div>
            <div className="font-grotesk font-bold text-current text-[12px] uppercase tracking-wider mb-3">
              支援詞庫
            </div>
            <div className="flex flex-col gap-2.5 font-medium">
              <Link to="/wordbook" className="hover:text-current no-underline text-current/70 transition-colors">
                HKDSE 英文 (3,000詞)
              </Link>
              <Link to="/level-test/intro" className="hover:text-current no-underline text-current/70 transition-colors">
                IELTS 雅思 (4,500詞)
              </Link>
              <Link to="/level-test/intro" className="hover:text-current no-underline text-current/70 transition-colors">
                TOEFL 托福 (5,000詞)
              </Link>
              <Link to="/deck/new" className="hover:text-current no-underline text-current/70 transition-colors">
                ＋ 建立自訂詞單
              </Link>
            </div>
          </div>

          {/* Column: 學習與帳戶 */}
          <div>
            <div className="font-grotesk font-bold text-current text-[12px] uppercase tracking-wider mb-3">
              學習與帳戶
            </div>
            <div className="flex flex-col gap-2.5 font-medium">
              <Link to="/today" className="hover:text-current no-underline text-current/70 transition-colors">
                今日學習任務
              </Link>
              <Link to="/mistakes" className="hover:text-current no-underline text-current/70 transition-colors">
                弱項錯題重溫本
              </Link>
              <Link to="/stats" className="hover:text-current no-underline text-current/70 transition-colors">
                學習記憶曲線統計
              </Link>
              <Link to="/login" className="hover:text-current no-underline text-current/70 transition-colors">
                學生帳戶登入
              </Link>
              <Link to="/settings" className="hover:text-current no-underline text-current/70 transition-colors">
                帳戶偏好與雲端同步
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto pt-6 border-t border-current/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[12px] opacity-70">
          <div>© {new Date().getFullYear()} Mango Word. Built for serious exam candidates.</div>
          <div className="flex items-center gap-6">
            <a href="#product" className="hover:text-current no-underline text-current/70 hover:underline transition-colors">
              產品架構
            </a>
            <a href="#methodology" className="hover:text-current no-underline text-current/70 hover:underline transition-colors">
              間隔演算法
            </a>
            <a href="#comparison" className="hover:text-current no-underline text-current/70 hover:underline transition-colors">
              功能對比
            </a>
            <a href="#pricing" className="hover:text-current no-underline text-current/70 hover:underline transition-colors">
              收費方案
            </a>
            <a href="#faq" className="hover:text-current no-underline text-current/70 hover:underline transition-colors">
              常見問題 FAQ
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
